import type { IUserService, TermsSurface } from "@/services/interfaces/IUserService";
import { validateImageFile } from "@/lib/questImages";
import { ProfileImageError, type ProfileImageKind } from "@/lib/profileImages";
import { pickWritableProfileFields } from "@/lib/profileUpdateColumns";
import { mockQuestAffinityFeatures } from "./MockQuestService";
import type {
  UserProfile,
  PublicProfile,
  ReputationDetail,
  LeaderboardEntry,
  PlatformStats,
  TrackRecord,
  QuestInterest,
  QuestInterestKind,
  SetQuestInterestInput,
  QuestAffinity,
  QuestAffinityAnswer,
} from "@/lib/types";

const MOCK_DELAY_MS = 800;

/**
 * A believable track record so the profile charts can be seen offline. The
 * activity is deterministic (no Math.random) so snapshots and screenshots are
 * stable across runs.
 */
function mockTrackRecord(): TrackRecord {
  const activity: TrackRecord["activity"] = [];
  const today = Date.UTC(
    new Date().getUTCFullYear(),
    new Date().getUTCMonth(),
    new Date().getUTCDate(),
  );
  for (let i = 0; i < 365; i++) {
    const seed = (i * 7919) % 23;
    if (seed > 8) continue;
    const worked = seed % 4;
    // Roughly one day in three is volunteer work.
    const volunteer = i % 3 === 0 ? worked : 0;
    activity.push({
      day: new Date(today - i * 86_400_000).toISOString().slice(0, 10),
      worked,
      paid: worked - volunteer,
      volunteer,
      launched: seed === 0 ? 1 : 0,
    });
  }
  return {
    worker: {
      paidTasks: 14,
      volunteerTasks: 6,
      paidHours: 72,
      volunteerHours: 30,
      questsJoined: 11,
      creatorsServed: 8,
    },
    creator: {
      questsLaunched: 4,
      questsDelivered: 3,
      questsCompleted: 3,
      paidQuests: 3,
      volunteerQuests: 1,
      tasksApproved: 9,
      workersHired: 5,
    },
    // One entry per approved task, as the RPC returns them.
    taskSkills: [
      ...Array.from({ length: 6 }, () => ["React", "TypeScript"]),
      ["React"],
      ["UI Design", "Technical Writing"],
      ["UI Design"],
      ["UI Design", "Technical Writing"],
      ["Technical Writing"],
      ["Data Analysis"],
      ["Data Analysis", "Community Management"],
      ["Community Management"],
      ["Beach Cleanup", "Tutoring"],
      ["plumbing", "handyman"],
      ["local-errands", "delivery"],
      ["limpieza"],
      ["photography"],
    ],
    activity: activity.filter((a) => a.worked + a.launched > 0),
  };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// 0–100, matching the contract's scale once formatReputationScore has divided
// out the basis points. The previous values (4.6 overall, 0.92 completion) were
// a 0–5 star scale mixed with a 0–1 ratio — three scales for one concept, and a
// mock-backed run rendered a 4.6%-wide bar that looked merely wrong rather than
// reproducing what real data does.
//
// The shape is deliberately the common real one: a worker past their first
// approved task saturates Completion/Quality/Reliability at 100 (see
// ReputationDetail's note), leaving speedScore as the only graded axis.
const MOCK_REPUTATION: ReputationDetail = {
  overall: 96.71,
  completionRate: 100,
  qualityScore: 100,
  speedScore: 83.56,
  reliability: 100,
  tasksCompleted: 3,
  tasksDisputed: 0,
  questsCreated: 0,
  questsCompleted: 26,
  lastUpdated: 1789526829,
};

// What UserRegistry.initializeReputation leaves behind: every axis at
// DEFAULT_SCORE and no history. Not zeros — 50 is a real starting score.
const MOCK_BASELINE_REPUTATION: ReputationDetail = {
  overall: 50,
  completionRate: 50,
  qualityScore: 50,
  speedScore: 50,
  reliability: 50,
  tasksCompleted: 0,
  tasksDisputed: 0,
  questsCreated: 0,
  questsCompleted: 0,
  lastUpdated: 1776630312,
};

const MOCK_USER_PROFILE: UserProfile = {
  id: "mock-user-123",
  handle: "mockhunter",
  displayName: "Mock Hunter",
  email: "hunter@wexare.dev",
  avatarUrl: null,
  bio: "Full-stack developer with a passion for decentralized platforms. Solidity, React, and TypeScript enthusiast.",
  skills: ["React", "TypeScript", "Solidity", "Tailwind CSS", "Node.js"],
  needs: [],
  walletAddress: "0xMOCK_WALLET_ADDRESS_1234",
  rolePreference: "both",
  recurringMatch: true,
  activityLevel: "active",
  preferredCurrency: "USDT",
  preferredLanguage: "en",
  reputation: { ...MOCK_REPUTATION },
  role: "user",
  locationCountry: null,
  locationCity: null,
  createdAt: new Date(Date.now() - 90 * 86400000).toISOString(),
};

const MOCK_LEADERBOARD: LeaderboardEntry[] = [
  {
    userId: "mock-leader-001",
    displayName: "Elena Coder",
    avatarUrl: null,
    reputation: 4.95,
    completedBounties: 47,
  },
  {
    userId: "mock-leader-002",
    displayName: "Marcus Dev",
    avatarUrl: null,
    reputation: 4.88,
    completedBounties: 39,
  },
  {
    userId: "mock-user-123",
    displayName: "Mock Hunter",
    avatarUrl: null,
    reputation: 4.6,
    completedBounties: 22,
  },
  {
    userId: "mock-leader-003",
    displayName: "Sofia Writer",
    avatarUrl: null,
    reputation: 4.55,
    completedBounties: 31,
  },
  {
    userId: "mock-leader-004",
    displayName: "Kai Designer",
    avatarUrl: null,
    reputation: 4.42,
    completedBounties: 18,
  },
  {
    userId: "mock-leader-005",
    displayName: "Liam Auditor",
    avatarUrl: null,
    reputation: 4.3,
    completedBounties: 15,
  },
  {
    userId: "mock-leader-006",
    displayName: "Aria Tester",
    avatarUrl: null,
    reputation: 4.25,
    completedBounties: 27,
  },
  {
    userId: "mock-leader-007",
    displayName: "Noah Analyst",
    avatarUrl: null,
    reputation: 4.1,
    completedBounties: 12,
  },
  {
    userId: "mock-leader-008",
    displayName: "Zara Researcher",
    avatarUrl: null,
    reputation: 3.98,
    completedBounties: 9,
  },
  {
    userId: "mock-leader-009",
    displayName: "Ravi Builder",
    avatarUrl: null,
    reputation: 3.85,
    completedBounties: 8,
  },
];

export class MockUserService implements IUserService {
  private profiles: Map<string, UserProfile> = new Map();
  private questInterest: Map<QuestInterestKind, QuestInterest> = new Map();
  private questAffinity: Map<string, QuestAffinity> = new Map();
  private attentionDigest: Map<string, boolean> = new Map();

  constructor() {
    this.profiles.set(MOCK_USER_PROFILE.id, { ...MOCK_USER_PROFILE });
  }

  async getProfile(userId: string): Promise<UserProfile> {
    await delay(500);

    const profile = this.profiles.get(userId);
    if (!profile) {
      // Return a generated profile for unknown users
      return {
        id: userId,
        handle: `user-${userId.slice(-6)}`,
        displayName: `User ${userId.slice(-6)}`,
        email: `${userId.slice(-6)}@wexare.dev`,
        avatarUrl: null,
        bio: "",
        skills: [],
        needs: [],
        walletAddress: null,
        rolePreference: "both",
        recurringMatch: true,
        activityLevel: "casual",
        preferredCurrency: "USDT",
        preferredLanguage: null,
        reputation: {
          overall: 0,
          completionRate: 0,
          qualityScore: 0,
          speedScore: 0,
          reliability: 0,
        },
        role: "user",
        locationCountry: null,
        locationCity: null,
        createdAt: new Date().toISOString(),
      };
    }
    return { ...profile };
  }

  async updateProfile(
    userId: string,
    data: Partial<UserProfile>,
  ): Promise<UserProfile> {
    await delay(MOCK_DELAY_MS);

    // The same whitelist as the Supabase adapter: a field prod drops (email,
    // the banner) must not stick here and pass a test prod would fail.
    return this.writeProfile(userId, pickWritableProfileFields(data));
  }

  private async writeProfile(userId: string, fields: Partial<UserProfile>): Promise<UserProfile> {
    const existing = await this.getProfile(userId);
    const updated: UserProfile = {
      ...existing,
      ...fields,
      id: userId,
    };
    this.profiles.set(userId, updated);
    return { ...updated };
  }

  async getPublicProfile(handle: string): Promise<PublicProfile | null> {
    await delay(500);

    const profile = Array.from(this.profiles.values()).find(
      (p) => p.handle === handle,
    );

    if (!profile) {
      if (handle.endsWith("-missing")) return null;
      // Synthesize, as getProfile does, so any /u/<handle> renders in mock mode.
      // The id ends with the handle, so getTrackRecord's `-new` suffix still
      // reaches the empty record from `/u/anyone-new`.
      return {
        id: `did:mock:${handle}`,
        handle,
        displayName: handle,
        avatarUrl: null,
        bannerUrl: null,
        bio: "",
        skills: ["React", "TypeScript", "Figma", "Tutoring"],
        rolePreference: "both",
        reputation: { overall: 0, completionRate: 0, qualityScore: 0, speedScore: 0, reliability: 0 },
        completedBounties: 0,
        questsCreated: 0,
        memberSince: new Date("2026-01-15").toISOString(),
        // `/u/<anything>-agent` renders an AI agent owned by the mock user.
        accountType: handle.endsWith("-agent") ? "agent" : "human",
        agentOwnerId: handle.endsWith("-agent") ? "user-dev-1" : null,
      };
    }

    return {
      id: profile.id,
      handle: profile.handle,
      displayName: profile.displayName,
      avatarUrl: profile.avatarUrl,
      bannerUrl: profile.bannerUrl ?? null,
      bio: profile.bio,
      skills: profile.skills,
      rolePreference: profile.rolePreference,
      reputation: { ...profile.reputation },
      completedBounties: 22,
      questsCreated: 5,
      memberSince: profile.createdAt,
      accountType: "human",
      agentOwnerId: null,
    };
  }

  async getPublicProfileById(profileId: string): Promise<PublicProfile | null> {
    if (profileId.endsWith("-missing")) {
      await delay(500);
      return null;
    }
    const known = this.profiles.get(profileId);
    // Unknown ids synthesize a handle from the id's tail, as getPublicProfile
    // does for handles, so any mock quest's creator opens a profile.
    const handle = known?.handle || profileId.split(":").pop() || profileId;
    const profile = await this.getPublicProfile(handle);
    return profile && { ...profile, id: profileId };
  }

  async getAgentsOwnedBy(
    ownerId: string,
  ): Promise<{ agentDid: string; name: string; status: "active" | "paused" }[]> {
    // Both mock identities: the dev session user and the /u/mockhunter profile.
    return ownerId === "user-dev-1" || ownerId === "mock-user-123"
      ? [{ agentDid: "did:mock:scout-agent", name: "Scout", status: "active" }]
      : [];
  }

  async getTrackRecord(profileId: string): Promise<TrackRecord | null> {
    await delay(500);
    // Same suffix convention as getReputation, so the not-found and empty
    // states are reachable in dev: `/u/anyone-missing`, `/u/anyone-new`.
    if (profileId.endsWith("-missing")) return null;
    if (profileId.endsWith("-new")) {
      return {
        worker: { paidTasks: 0, volunteerTasks: 0, paidHours: 0, volunteerHours: 0, questsJoined: 0, creatorsServed: 0 },
        creator: { questsLaunched: 0, questsDelivered: 0, questsCompleted: 0, paidQuests: 0, volunteerQuests: 0, tasksApproved: 0, workersHired: 0 },
        taskSkills: [],
        activity: [],
      };
    }
    return mockTrackRecord();
  }

  async getTrackRecordForHandle(handle: string, profileId: string): Promise<TrackRecord | null> {
    // Mock ids end with their handle (see getPublicProfile), so a handle that
    // doesn't belong to the id answers null, as the real RPC does.
    if (!profileId.endsWith(handle)) return null;
    return this.getTrackRecord(profileId);
  }

  async updateSkills(userId: string, skills: string[]): Promise<void> {
    // Via getProfile, like updateProfile: it synthesizes unknown ids (the mock
    // sign-in user among them), so a skills save must accept the same ids.
    const profile = await this.getProfile(userId);
    this.profiles.set(userId, { ...profile, skills });
  }

  async addSkills(userId: string, skills: string[]): Promise<void> {
    if (skills.length === 0) return;

    const profile = await this.getProfile(userId);
    // Additive and de-duplicated, mirroring SupabaseUserService.addSkills — the
    // mock adapter is what offline mode and the tests run against, so a mock
    // that replaced here would hide the very bug the real one was fixed for.
    this.profiles.set(userId, {
      ...profile,
      skills: [...new Set([...profile.skills, ...skills])],
    });
  }

  async getReputation(userId: string): Promise<ReputationDetail | null> {
    await delay(500);

    // Synthesize for an unknown user, exactly as getProfile does above. The
    // mock session is `user-dev-1` (useAuthStore) while this map is seeded with
    // `mock-user-123`, so gating on `profiles.has(userId)` meant mock mode
    // showed the empty state to every developer and the populated card could
    // not be seen locally at all.
    //
    // The suffixes make the other two arms reachable in dev without editing
    // this file; the real adapter derives them from the chain.
    if (userId.endsWith("-unregistered")) return null;
    if (userId.endsWith("-new")) return { ...MOCK_BASELINE_REPUTATION };

    return { ...MOCK_REPUTATION };
  }

  async getLeaderboard(_category?: string): Promise<LeaderboardEntry[]> {
    await delay(MOCK_DELAY_MS);

    // Return the full leaderboard; category filtering is a no-op in mock
    return MOCK_LEADERBOARD.map((entry) => ({ ...entry }));
  }

  async setProfileImage(userId: string, kind: ProfileImageKind, file: File): Promise<UserProfile> {
    await delay(MOCK_DELAY_MS);
    // Same client-side gate as the real adapter, so the mock fails where prod would.
    const check = validateImageFile(file);
    if (!check.ok) {
      throw new ProfileImageError(check.reason === "too_large" ? "too_large" : "bad_type", check.reason);
    }
    // An object URL stands in for the bucket: it renders, and dies with the tab.
    const url = URL.createObjectURL(file);
    // writeProfile, not updateProfile: the banner is outside the owner's
    // whitelist, exactly as banner_path is set only by the real image path.
    await delay(MOCK_DELAY_MS);
    return this.writeProfile(userId, kind === "avatar" ? { avatarUrl: url } : { bannerPath: url, bannerUrl: url });
  }

  async clearProfileImage(userId: string, kind: ProfileImageKind): Promise<UserProfile> {
    await delay(MOCK_DELAY_MS);
    return this.writeProfile(userId, kind === "avatar" ? { avatarUrl: null } : { bannerPath: null, bannerUrl: null });
  }

  async markOnboarded(_userId: string): Promise<void> {
    await delay(MOCK_DELAY_MS);
    // No-op in mock — localStorage handles it
  }

  async recordTermsAcceptance(_userId: string, _version: string, _surface: TermsSurface): Promise<void> {
    await delay(MOCK_DELAY_MS);
    // No-op in mock — there is no Privy login to accept at.
  }

  async getPlatformStats(): Promise<PlatformStats> {
    await delay(MOCK_DELAY_MS);
    return {
      activeContributors: 127,
      tasksCompletedThisWeek: 342,
      totalPaymentsReleased: 48500,
    };
  }

  async getCollaboratorCount(skills: string[], needs: string[]): Promise<number> {
    await delay(MOCK_DELAY_MS);
    return Math.max(3, Math.floor(skills.length * 7 + needs.length * 5 + Math.random() * 20));
  }

  async getQuestInterest(_userId: string, kind: QuestInterestKind): Promise<QuestInterest | null> {
    await delay(MOCK_DELAY_MS);
    const row = this.questInterest.get(kind);
    return row ? { ...row } : null;
  }

  async setQuestInterest(input: SetQuestInterestInput): Promise<QuestInterest> {
    await delay(MOCK_DELAY_MS);
    // Mirrors set_quest_interest: answeredAt and the first source survive a
    // later change from Profile.
    const prev = this.questInterest.get(input.kind);
    const now = new Date().toISOString();
    const row: QuestInterest = {
      kind: input.kind,
      wantsAlerts: input.wantsAlerts,
      wantsMatching: input.wantsMatching,
      sourceQuestId: input.sourceQuestId ?? prev?.sourceQuestId ?? null,
      answeredAt: prev?.answeredAt ?? now,
      updatedAt: now,
    };
    this.questInterest.set(input.kind, row);
    return { ...row };
  }

  async getQuestAffinities(_userId: string): Promise<QuestAffinity[]> {
    await delay(MOCK_DELAY_MS);
    return [...this.questAffinity.values()]
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .map((r) => ({ ...r, skills: [...r.skills] }));
  }

  async setQuestAffinity(
    questId: string,
    answer: QuestAffinityAnswer | null,
  ): Promise<QuestAffinity | null> {
    await delay(MOCK_DELAY_MS);
    if (answer === null) {
      this.questAffinity.delete(questId);
      return null;
    }
    // Mirrors set_quest_affinity's server-side snapshot of the quest.
    const features = mockQuestAffinityFeatures(questId) ?? {
      category: "",
      skills: [],
      isVolunteer: false,
    };
    const row: QuestAffinity = {
      questId,
      answer,
      ...features,
      updatedAt: new Date().toISOString(),
    };
    this.questAffinity.set(questId, row);
    return { ...row, skills: [...row.skills] };
  }

  async getAttentionDigestEnabled(userId: string): Promise<boolean> {
    await delay(MOCK_DELAY_MS);
    return this.attentionDigest.get(userId) ?? true;
  }

  async setAttentionDigestEnabled(userId: string, enabled: boolean): Promise<void> {
    await delay(MOCK_DELAY_MS);
    this.attentionDigest.set(userId, enabled);
  }
}
